import type * as React from 'react';

import type { TooltipContent } from '../../../tooltip';

// ============================================================================
// Constants
// ============================================================================

/**
 * Panel width (16rem / 256px)
 */
export const PANEL_WIDTH = '16rem';

/**
 * localStorage key for panel pinned state per page.
 * Actual persistence is handled by usePinnedState hook in hooks/usePageShellState.ts
 */
export const PANEL_STORAGE_KEY = 'heidi-pinned-panels';

/**
 * Keyboard shortcut for toggling panel pinned state.
 * Used with Cmd (Mac) or Ctrl (Windows/Linux).
 */
export const PANEL_KEYBOARD_SHORTCUT = '/';

/**
 * Static ID for the pinned panel element.
 * Used for aria-controls linking from trigger.
 */
export const PANEL_PINNED_ID = 'panel-pinned';

// ============================================================================
// Context Types
// ============================================================================

/**
 * Mobile view states for the panel drawer
 */
export type PanelMobileView = 'sidebar' | 'panel';

/**
 * Registered panel data containing content and optional action
 */
export interface PanelRegistration {
  /**
   * Panel content to display
   */
  content: React.ReactNode;

  /**
   * Optional action button to display in panel header (left-aligned)
   */
  action?: React.ReactNode;
}

/**
 * Panel context value provided to all panel components
 */
export interface PanelContextValue {
  /**
   * Whether the panel is currently pinned (global state).
   * When true, the current page's panel is shown in pinned position.
   */
  isPinned: boolean;

  /**
   * Set the pinned state.
   * Pass true to pin the current page's panel, false to unpin.
   */
  setIsPinned: (pinned: boolean) => void;

  /**
   * Focus the pinned panel (for accessibility).
   * Call this after opening the panel to move focus there.
   */
  focusPanel: () => void;

  /**
   * Register the pinned panel element for focus management.
   * Called by PanelPinned to register its DOM element.
   */
  setPanelRef: (element: HTMLElement | null) => void;

  /**
   * ID of the currently showing floating panel (null if none)
   */
  floatingPanelId: string | null;

  /**
   * Set the floating panel ID.
   * Accepts direct value or functional update for current state access.
   */
  setFloatingPanelId: React.Dispatch<React.SetStateAction<string | null>>;

  /**
   * Content currently displayed in the active panel
   */
  activePanelContent: React.ReactNode | null;

  /**
   * Set the active panel content
   */
  setActivePanelContent: (content: React.ReactNode | null) => void;

  /**
   * Action button currently displayed in the active panel header (mobile only)
   */
  activePanelAction: React.ReactNode | null;

  /**
   * Set the active panel action
   */
  setActivePanelAction: (action: React.ReactNode | null) => void;

  /**
   * Map of registered panel data by ID
   */
  registeredPanels: Map<string, PanelRegistration>;

  /**
   * Register a panel with its content and optional action
   */
  registerPanel: (id: string, content: React.ReactNode, action?: React.ReactNode) => void;

  /**
   * Unregister a panel
   */
  unregisterPanel: (id: string) => void;

  /**
   * Whether the device is mobile
   */
  isMobile: boolean;

  /**
   * Current mobile view state
   */
  mobileView: PanelMobileView;

  /**
   * Set the mobile view state
   */
  setMobileView: (view: PanelMobileView) => void;

  /**
   * Current page identifier for page-specific pinned state
   */
  pageId: string;

  /**
   * Whether panel content is available for the current page (for toggle button visibility).
   * Returns true only if a panel with an ID matching `pageId` has been registered.
   */
  hasPanelContent: boolean;
}

// ============================================================================
// Component Props
// ============================================================================

/**
 * Panel provider props
 */
export interface PanelProviderProps {
  /**
   * Current page identifier for page-specific pinned state
   */
  pageId: string;

  /**
   * Children to render within the panel context
   */
  children: React.ReactNode;
}

/**
 * Panel trigger button props
 */
export interface PanelTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Label shown in tooltip. Consumer should manage this based on open/closed state.
   * @default "Toggle panel"
   */
  label?: string;

  /**
   * Test identifier for debugging and testing
   */
  'data-testid'?: string;
}

/**
 * Panel floating (NavigationMenu) props
 */
export interface PanelFloatingProps {
  /**
   * Unique identifier for this panel
   */
  id: string;

  /**
   * Trigger element (typically a nav item button)
   */
  trigger: React.ReactNode;

  /**
   * Panel content to display.
   *
   * **Important:** Memoize this value to prevent unnecessary re-registrations.
   * If `content` is not memoized, a new reference will be created on each parent
   * render, causing the panel to re-register and potentially triggering re-renders
   * in context consumers.
   *
   * @example
   * ```tsx
   * // ✅ Good - memoized content
   * const panelContent = React.useMemo(() => <MyPanelContent data={data} />, [data]);
   * <PanelFloating content={panelContent} ... />
   *
   * // ❌ Bad - creates new reference on every render
   * <PanelFloating content={<MyPanelContent data={data} />} ... />
   * ```
   */
  content: React.ReactNode;

  /**
   * Optional action button to display in the floating panel header (left-aligned).
   * Typically used for primary actions like "New chat", "Add task", etc.
   *
   * @example
   * ```tsx
   * <PanelFloating
   *   action={
   *     <Button variant="outline" size="sm">
   *       <Plus className="size-4" />
   *       Add task
   *     </Button>
   *   }
   *   content={<TasksPanel />}
   * />
   * ```
   */
  action?: React.ReactNode;

  /**
   * Optional callback when the maximize button is clicked.
   * When provided, clicking maximize will navigate to the panel's page and pin it.
   * If not provided, maximize button will only pin the panel without navigation.
   *
   * @example
   * ```tsx
   * <PanelFloating
   *   id="tasks"
   *   onNavigate={(pageId) => router.push(`/${pageId}`)}
   *   content={<TasksPanel />}
   * />
   * ```
   */
  onNavigate?: (pageId: string) => void;

  /**
   * Optional tooltip props to show alongside the panel
   */
  tooltipProps?: React.ComponentProps<typeof TooltipContent> | null;

  /**
   * Whether to hide the tooltip (collapsed state check)
   */
  hideTooltip?: boolean;

  /**
   * Test identifier for debugging and testing
   */
  'data-testid'?: string;
}

/**
 * Panel pinned props
 */
export interface PanelPinnedProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Test identifier for debugging and testing
   */
  'data-testid'?: string;
}

/**
 * Panel mobile props
 */
export interface PanelMobileProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Sidebar content to display in mobile drawer
   */
  sidebarContent?: React.ReactNode;

  /**
   * Test identifier for debugging and testing
   */
  'data-testid'?: string;
}

// ============================================================================
// Panel Types
// ============================================================================

/**
 * Panel props - a pure styling component for secondary panels.
 * This is a "dumb" component that provides consistent visual styling
 * without any state management or context dependencies.
 */
export interface PanelProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * HTML id attribute for the panel element.
   * Used for skip link targeting (e.g., href="#panel-id").
   */
  id?: string;

  /**
   * Whether to apply inset design styling (e.g., vertical padding).
   * Passed from the call site based on the sidebar variant.
   */
  isInset?: boolean;

  /**
   * Whether the panel is pinned (in document flow). Defaults to true.
   * - true: Renders in document flow, pushes content aside
   * - false: Fixed positioning, overlays content (shadow added)
   */
  isPinned?: boolean;

  /**
   * Panel width. Defaults to PANEL_WIDTH (16rem / 256px).
   * Accepts any valid CSS width value.
   */
  width?: string | number;

  /**
   * Panel state for animations and accessibility.
   * - 'open': Panel is visible
   * - 'closed': Panel is hidden (used for exit animations)
   */
  state?: 'open' | 'closed';

  /**
   * Whether content is switching (disables transitions).
   * Set to true when swapping content to prevent animation flash.
   */
  switching?: boolean;

  /**
   * Test identifier for debugging and testing
   */
  'data-testid'?: string;
}
